import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/useAuth';
import { RequireRole } from '../auth/RequireRole';
import { InviteSection } from './admin/InviteSection';
import { UserManagement } from './admin/UserManagement';
import { AdminBranding } from './admin/AdminBranding';
import { LevelingAchievementsSettings } from './admin/LevelingAchievementsSettings';
import { AggregatedUsageTracker } from './admin/AggregatedUsageTracker';
import { CacheManagement } from './admin/CacheManagement';
import { TeamBilling } from './billing/TeamBilling';
import { DemoAccounts } from './admin/DemoAccounts';
import { BILLING_ENABLED } from '../config/plans';
import { FirebaseUsageTracker } from './FirebaseUsageTracker';
import { trackRead } from '../services/firebaseUsageTracker';

// Shared type for admin config (branding + leveling + achievements)
export type AdminConfig = {
  casinoName?: string;
  leveling?: {
    baseXP: number;
    increment: number;
    maxLevel: number;
    stages: { level: number; title: string }[];
  };
  customAchievements?: unknown[];
  achievementEmojiOptions?: string[];
};

type TabId = 'invites' | 'users' | 'settings' | 'gamification' | 'billing' | 'demos' | 'usage';

type Tab = {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  devOnly?: boolean;
  /** Only shown when billing is live. */
  billingOnly?: boolean;
};

const TABS: Tab[] = [
  {
    id: 'invites',
    label: 'Invites',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    id: 'users',
    label: 'Users',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
  },
  {
    id: 'settings',
    label: 'Branding',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    id: 'gamification',
    label: 'XP & Achievements',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ),
  },
  {
    id: 'billing',
    label: 'Team Plan',
    billingOnly: true,
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
  },
  {
    id: 'demos',
    label: 'Demos',
    devOnly: true,
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    id: 'usage',
    label: 'Firebase Usage',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    devOnly: true,
  },
];

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const AdminPanelModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { roles, profile } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('invites');
  const isDev = roles.includes('dev');
  const isAdmin = roles.includes('admin');

  // Track which tabs have been visited (for lazy mounting)
  const [visitedTabs, setVisitedTabs] = useState<Set<TabId>>(new Set(['invites']));

  // Cached admin config - loaded once when modal opens
  const [adminConfig, setAdminConfig] = useState<AdminConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);

  // Mark tab as visited when switching
  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    setVisitedTabs(prev => {
      if (prev.has(tabId)) return prev;
      const next = new Set(prev);
      next.add(tabId);
      return next;
    });
  };

  // Filter tabs based on role and whether billing is live
  const visibleTabs = TABS.filter(
    tab => (!tab.devOnly || isDev) && (!tab.billingOnly || BILLING_ENABLED),
  );

  // Load admin config ONCE when modal opens
  useEffect(() => {
    if (!isOpen || !profile?.uid || (!isAdmin && !isDev)) {
      setConfigLoading(false);
      return;
    }

    // Only load if we haven't loaded yet
    if (adminConfig !== null) {
      setConfigLoading(false);
      return;
    }

    const loadConfig = async () => {
      try {
        const configRef = doc(db, 'adminConfigs', profile.uid);
        const snap = await getDoc(configRef);
        trackRead('adminConfigs', 'admin-panel-load');
        
        if (snap.exists()) {
          setAdminConfig(snap.data() as AdminConfig);
        } else {
          // No config yet - set empty
          setAdminConfig({});
        }
      } catch (err) {
        console.warn('Failed to load admin config:', err);
        setAdminConfig({});
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, [isOpen, profile?.uid, isAdmin, isDev, adminConfig]);

  // Handler for children to update cache after save
  const handleConfigUpdate = (updates: Partial<AdminConfig>) => {
    setAdminConfig(prev => prev ? { ...prev, ...updates } : updates);
  };

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <RequireRole roles={['admin', 'dev']} fallback={null}>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-y-0 right-0 w-full sm:w-[600px] lg:w-[800px] bg-gray-900 border-l border-gray-800 z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex-none border-b border-gray-800 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                <span className="text-2xl">🎰</span>
                Admin Panel
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                {isDev ? 'Developer Access' : isAdmin ? 'Admin Access' : 'Restricted'}
                {profile?.email && <span className="ml-2 text-gray-500">({profile.email})</span>}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
              aria-label="Close admin panel"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 overflow-x-auto pb-1">
            {visibleTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content - tabs stay mounted once visited (hidden when inactive) */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {/* Invites - always mounted first */}
          <div className={activeTab === 'invites' ? '' : 'hidden'}>
            <InviteSection />
          </div>

          {/* Users - lazy mount, then keep mounted */}
          {visitedTabs.has('users') && (
            <div className={activeTab === 'users' ? '' : 'hidden'}>
              <UserManagement />
            </div>
          )}

          {/* Branding - lazy mount */}
          {visitedTabs.has('settings') && (
            <div className={activeTab === 'settings' ? '' : 'hidden'}>
              <AdminBranding 
                config={adminConfig} 
                configLoading={configLoading}
                onConfigUpdate={handleConfigUpdate}
              />
            </div>
          )}

          {/* Gamification - lazy mount */}
          {visitedTabs.has('gamification') && (
            <div className={activeTab === 'gamification' ? '' : 'hidden'}>
              <LevelingAchievementsSettings 
                config={adminConfig}
                configLoading={configLoading}
                onConfigUpdate={handleConfigUpdate}
              />
            </div>
          )}

          {/* Team plan / billing - only when billing is live, lazy mount */}
          {BILLING_ENABLED && visitedTabs.has('billing') && (
            <div className={activeTab === 'billing' ? '' : 'hidden'}>
              <TeamBilling />
            </div>
          )}

          {/* Demos - dev only, lazy mount */}
          {isDev && visitedTabs.has('demos') && (
            <div className={activeTab === 'demos' ? '' : 'hidden'}>
              <DemoAccounts />
            </div>
          )}

          {/* Usage - dev only, lazy mount */}
          {isDev && visitedTabs.has('usage') && (
            <div className={activeTab === 'usage' ? '' : 'hidden'}>
              <div className="space-y-4">
                {/* Cache Management */}
                <CacheManagement />
                
                {/* Aggregated usage across all users */}
                <AggregatedUsageTracker expanded />
                
                {/* Your personal session usage */}
                <div className="border-t border-gray-700 pt-4">
                  <h4 className="text-sm font-semibold text-gray-300 mb-3">Your Session (Local Tracking)</h4>
                  <FirebaseUsageTracker expanded />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-none border-t border-gray-800 p-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </RequireRole>
  );
};
